import { useEffect, useState } from "react";
import { RefreshCw, Loader2, AlertTriangle, Package, Layers } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

/**
 * RenderQueuePanel
 * ----------------
 * Live ops view of the studio-render pipeline. Auto-refreshes every 5 s
 * so an admin can watch a bulk upload move through the queue in real
 * time. Turns the black-box render pipeline into an ops surface.
 */
export default function RenderQueuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/render/queue");
      setData(r.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);   // live tick — bulk uploads move fast
    return () => clearInterval(t);
  }, []);

  const retry = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/seller/listings/${id}/photos/regenerate`);
      toast.success("Regenerate queued — this row will update as photos land.");
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusyId(""); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading queue…</div>;
  }
  if (!data) return null;

  const { stats, in_flight, failed, needs_attention, bulk_sessions, live_jobs } = data;

  const Stat = ({ label, value, tone = "" }) => (
    <div className={`border border-border rounded-md p-4 ${tone}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );

  return (
    <section data-testid="admin-render-queue">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl font-bold">Render Queue</h2>
          <p className="text-xs text-muted-foreground">Live view of studio renders in flight, recent failures, and bulk upload sessions. Auto-refresh 5 s.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
          data-testid="render-queue-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Backlog alert — surfaces when the queue depth exceeds pod
          concurrency by the configured threshold OR when any running
          job has been stuck > 3 min. Full-width red banner so ops can't
          miss it. */}
      {(stats.alert_backlog || stats.alert_stuck_jobs > 0) && (
        <div
          className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 flex items-start gap-2"
          data-testid="render-queue-backlog-alert"
        >
          <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
          <div className="text-[12px] text-destructive font-medium leading-snug">
            {stats.alert_backlog ? (
              <>Queue backed up — {stats.queue_depth_over_capacity} job(s) waiting beyond capacity of {stats.concurrency}. Sellers are seeing longer waits.</>
            ) : null}
            {stats.alert_stuck_jobs > 0 ? (
              <> {stats.alert_stuck_jobs} job(s) have been running &gt;3 min — investigate for a hang.</>
            ) : null}
          </div>
        </div>
      )}

      {/* Live-jobs row — jobs currently running or queued in
          `render_jobs` (source of truth for progress-bar polling).
          Separate from the legacy `in_flight` cohort (based on
          listings.processing) which stays for backward-compat visibility. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <Stat
          label="Running now"
          value={stats.live_running ?? 0}
          tone={stats.live_running > 0 ? "border-primary/50 bg-primary/5" : ""}
        />
        <Stat
          label="Queued"
          value={stats.live_queued ?? 0}
          tone={stats.live_queued > 0 ? "border-amber-500/50 bg-amber-500/5" : ""}
        />
        <Stat label="Concurrency" value={stats.concurrency ?? "—"} />
        <Stat
          label="Avg render (last 100)"
          value={stats.avg_render_seconds != null ? `${stats.avg_render_seconds}s` : "—"}
        />
        <Stat
          label="p95 render"
          value={stats.p95_render_seconds != null ? `${stats.p95_render_seconds}s` : "—"}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="In flight" value={stats.total_in_flight} tone={stats.total_in_flight > 0 ? "border-primary/50 bg-primary/5" : ""} />
        <Stat label="Failed · 7 d" value={stats.total_failed_7d} tone={stats.total_failed_7d > 0 ? "border-destructive/50 bg-destructive/5" : ""} />
        <Stat label="Partial (< 8 photos)" value={stats.total_needs_attention} tone={stats.total_needs_attention > 0 ? "border-amber-500/50 bg-amber-500/5" : ""} />
        <Stat label="Approved listings" value={stats.total_approved} />
        <Stat label="Embed coverage" value={`${stats.embed_coverage_pct}%`} tone={stats.embed_coverage_pct < 90 ? "border-amber-500/50 bg-amber-500/5" : ""} />
      </div>

      {/* Live progress rows — shows the actual render_jobs docs with
          percent + stage. Complements the legacy `in_flight` table which
          is based on the coarser listings.processing flag. */}
      {live_jobs && live_jobs.length > 0 ? (
        <div className="mt-2 mb-6" data-testid="render-queue-live">
          <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" /> Live jobs
            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{live_jobs.length}</span>
          </h3>
          <div className="overflow-x-auto border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Listing</th>
                  <th className="text-left px-3 py-2">Seller</th>
                  <th className="text-right px-3 py-2">Size</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-right px-3 py-2 w-[140px]">Progress</th>
                  <th className="text-right px-3 py-2">ETA</th>
                </tr>
              </thead>
              <tbody>
                {live_jobs.map((j) => (
                  <tr key={j.listing_id} className="border-t border-border" data-testid={`live-job-${j.listing_id}`}>
                    <td className="px-3 py-2">
                      <a href={`/listing/${j.listing_id}`} target="_blank" rel="noreferrer" className="hover:underline font-semibold">
                        {j.title}
                      </a>
                      <span className="ml-2 text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{j.trigger}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{j.seller_name || j.seller_email}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono">{j.size_mb ? `${j.size_mb} MB` : "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono">
                      <span className={j.status === "running" ? "text-primary" : "text-muted-foreground"}>
                        {j.stage_label || j.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-16 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full bg-primary transition-all" style={{ width: `${j.percent || 0}%` }} />
                        </div>
                        <span className="text-[11px] font-mono w-[36px] text-right">{j.percent || 0}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-mono">
                      {j.eta_seconds != null ? formatElapsed(j.eta_seconds) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <QueueTable
        icon={<Loader2 className="h-4 w-4 animate-spin text-primary" />}
        title="Currently rendering"
        rows={in_flight}
        emptyLabel="No renders running right now."
        columns={["title", "seller", "elapsed", "photos"]}
        testid="render-queue-in-flight"
      />

      <QueueTable
        icon={<AlertTriangle className="h-4 w-4 text-destructive" />}
        title="Recent failures (7 days)"
        rows={failed}
        emptyLabel="No failed renders in the last 7 days."
        columns={["title", "seller", "elapsed", "error"]}
        onRetry={retry}
        busyId={busyId}
        testid="render-queue-failed"
      />

      <QueueTable
        icon={<Package className="h-4 w-4 text-amber-600" />}
        title="Partial success (< 8 photos)"
        rows={needs_attention}
        emptyLabel="No partial-photo listings — every published design has the full 8."
        columns={["title", "seller", "elapsed", "photos"]}
        onRetry={retry}
        busyId={busyId}
        testid="render-queue-needs-attention"
      />

      {bulk_sessions.length > 0 && (
        <div className="mt-6" data-testid="render-queue-bulk-sessions">
          <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            Bulk upload sessions (7 days)
          </h3>
          <div className="overflow-x-auto border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Session</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Done</th>
                  <th className="text-right px-3 py-2">Pending</th>
                  <th className="text-right px-3 py-2">Failed</th>
                  <th className="text-right px-3 py-2">Partial</th>
                </tr>
              </thead>
              <tbody>
                {bulk_sessions.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`bulk-session-${b.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{b.id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-right">{b.total}</td>
                    <td className="px-3 py-2 text-right text-primary font-semibold">{b.done}</td>
                    <td className="px-3 py-2 text-right">{b.pending}</td>
                    <td className="px-3 py-2 text-right text-destructive">{b.failed}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{b.needs_attention}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function QueueTable({ icon, title, rows, emptyLabel, columns, onRetry, busyId, testid }) {
  return (
    <div className="mt-6" data-testid={testid}>
      <h3 className="font-display font-bold text-sm mb-2 flex items-center gap-2">
        {icon} {title}
        <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">{rows.length}</span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground pl-6">{emptyLabel}</p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Listing</th>
                <th className="text-left px-3 py-2">Seller</th>
                <th className="text-right px-3 py-2">Elapsed</th>
                {columns.includes("photos") && <th className="text-right px-3 py-2">Photos</th>}
                {columns.includes("error") && <th className="text-left px-3 py-2">Error</th>}
                {onRetry && <th className="text-right px-3 py-2">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`queue-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <a href={`/listing/${r.id}`} target="_blank" rel="noreferrer" className="hover:underline font-semibold">{r.title}</a>
                    {r.is_bulk && <span className="ml-2 text-[10px] font-mono text-primary uppercase tracking-widest">bulk</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.seller_name || r.seller_email}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{formatElapsed(r.elapsed_sec)}</td>
                  {columns.includes("photos") && <td className="px-3 py-2 text-right text-xs font-mono">{r.photos_count}/8</td>}
                  {columns.includes("error") && <td className="px-3 py-2 text-xs text-destructive max-w-[300px] truncate" title={r.render_error}>{r.render_error}</td>}
                  {onRetry && (
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onRetry(r.id)}
                        disabled={busyId === r.id}
                        data-testid={`queue-retry-${r.id}`}
                        className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                      >
                        {busyId === r.id ? "…" : "Regenerate"}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatElapsed(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  if (m < 60) return `${m}m${r > 0 ? ` ${r}s` : ""}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
